import { useState, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTabs, SttFullDialog } from '../../../sporttia/all';
import HandlePaymentsModal from '../../../pages/sc/groups/HandlePaymentsModal';
import {
    buildCancelBookingPaymentOptions,
    canDeleteBookingPayments,
    paymentActionForOption,
    useImpersonalCancelInfo,
} from './cancelBookingPaymentOptions';
import BookingDetailDialog from './BookingDetailDialog';
import BookingDialogPayments from './BookingDialogPayments';
import { getPath } from '../../../pages/Pages';
import BookingDialogDeviceAuths from './BookingDialogDeviceAuths';
import { buildBookingPdfUrl } from './buildBookingPdfUrl';
import { getErrorMessage, parsePrice } from '../../../lib/utils';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';
import { useInteractionsFiles, useAPI } from '../../../lib/hooks';
import { DefaultListResponse200 } from '../../../types/api/utils';
import { PriceShape } from '../../../types/payment';
import type { Facility } from '../../../types/models/Facility';
import type { User } from '../../../types/models/User';
import type { Occupant } from '../../../types/models/Occupant';
import type { Payment } from '../../../types/models/Payment';

// Local types
type BookingRate = {
    id: number;
    name: string;
    price?: number;
    duration?: number;
    priceShape?: PriceShape;
};

type BookingSlot = {
    id: number;
    schedule: {
        id: number;
        name: string;
    };
};

type BookingUser = {
    id: number;
    name: string;
    fullName: string;
    mship?: {
        id: number;
        name: string;
        fullName: string;
    };
};

export type Booking = {
    id: number;
    created: string;
    ini: string;
    end: string;
    name?: string;
    notes?: string;
    price: number;
    totalBill?: number;
    totalPaid?: number;
    priceShape?: PriceShape;
    totalPaidShape?: PriceShape;
    facility: Facility;
    user: BookingUser;
    creator?: User;
    cancelator?: {
        id: number;
        fullName: string;
    };
    rate?: BookingRate[];
    slot?: BookingSlot;
};

type BonoMov = {
    id: number;
    created: string;
    concept: string;
    boleto: {
        id: number;
        user: {
            id: number;
            name: string;
        };
        bono: {
            id: number;
            name: string;
        };
    };
};

type BookingDialogProps = {
    idBooking: number;
    onClose: () => void;
    onUpdate?: (booking?: Booking) => void;
    cutBooking?: ((booking: Booking) => void) | null;
};

/**
 *	Booking dialog
 *	==============
 *
 *	Cuando se abre el popup se carga el alquiler.
 *
 *	Props
 *
 *		* integer idBooking
 *		* function onClose
 *		* funcion onUpdate(booking): if the booking has been updated (deleted also)
 */
export default function BookingDialog({
    idBooking,
    onClose,
    onUpdate,
    cutBooking = null,
}: BookingDialogProps) {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { can } = usePermission();
    const canModifyBooking = can(PERMISSIONS.BOOKING.MODIFY);
    const canCancelBooking = can(PERMISSIONS.BOOKING.DELETE);
    const { openFile, downloadFile } = useInteractionsFiles();
    const navigate = useNavigate();
    const [booking, setBooking] = useState<Booking | null>(null);
    const [payments, setPayments] =
        useState<DefaultListResponse200<Payment> | null>(null);
    const [bonoMovs, setBonoMovs] =
        useState<DefaultListResponse200<BonoMov> | null>(null);
    const [onlyBonos, setOnlyBonos] = useState(false);
    const [openAskingPaymentProcedure, setOpenAskingPaymentProcedure] =
        useState(false);
    // Una entrada marcada como impersonal (al portador) solo puede devolverse en
    // efectivo o datáfono al cancelar (nunca monedero). Un alquiler que mezcla
    // entradas impersonales y nominales no puede cancelarse completo; hay que
    // cancelar cada entrada por separado.
    const [request] = useAPI(cxt?.migrationApis ?? null);

    const { data: occupantsResponse } = useQuery({
        queryKey: [`/bookings/${idBooking}/occupants`],
        queryFn: () =>
            request<DefaultListResponse200<Occupant>>(
                'GET',
                `/bookings/${idBooking}/occupants`,
            ),
        enabled: Boolean(idBooking),
    });

    const { isImpersonal, isMixedImpersonal, canRefundDataphone } =
        useImpersonalCancelInfo(occupantsResponse?.rows);

    useEffect(() => {
        if (payments && bonoMovs && payments.count <= 0 && bonoMovs.count > 0) {
            setOnlyBonos(true);
        } else setOnlyBonos(false);
    }, [payments, bonoMovs]);

    /**
     * Load booking
     */
    const loadBooking = () => {
        cxt?.api<{ booking: Booking }>('GET', `/bookings/${idBooking}`, {
            params: {
                trash: true,
            },
            success: (r: any) => {
                setBooking(r.booking);
            },
        });
    };

    /**
     * Load payments
     */
    const loadPayments = () => {
        cxt?.api<DefaultListResponse200<Payment>>(
            'GET',
            `/bookings/${idBooking}/payments`,
            {
                params: {
                    page: 1,
                    rows: 100,
                    trash: false,
                    returnChildPayments: true,
                },
                success: (r: any) => {
                    setPayments(r);
                },
            },
        );
    };

    /**
     * Load bono movs
     */
    const loadBonoMovs = () => {
        cxt?.api<DefaultListResponse200<BonoMov>>(
            'GET',
            `/bookings/${idBooking}/bonos/movs`,
            {
                params: { page: 1, rows: 100, trash: false },
                success: (r: any) => {
                    setBonoMovs(r);
                },
            },
        );
    };

    /**
     * Init
     */
    useEffect(() => {
        if (idBooking) {
            loadBooking();
            loadPayments();
            loadBonoMovs();
        } else setBooking(null);
    }, [idBooking]);

    /**
     * Save
     */
    const save = () => {
        if (!booking) return;

        cxt?.api<{ booking: Booking }>('PUT', `/bookings/${booking.id}`, {
            params: {
                ini: booking?.ini,
                end: booking?.end,
                idFacility: booking?.facility?.id ?? undefined,
                name: booking?.name,
                notes: booking?.notes,
                price: parsePrice(booking.price),
            },
            success: (r: any) => {
                cxt?.showMessage('S', t('Saved'));
                if (onUpdate) onUpdate(r.booking);
            },
        });
    };

    /**
     * Cancel booking
     */
    const cancelBooking = (deletingPaymentProcedure?: number) => {
        let bodyParams: Record<string, unknown> | undefined;
        if (deletingPaymentProcedure !== undefined) {
            bodyParams = {
                paymentAction: deletingPaymentProcedure,
            };
        }
        cxt?.api<{ booking: Booking }>('DELETE', `/bookings/${idBooking}`, {
            params: bodyParams,
            success: (r: any) => {
                onClose();
                setOpenAskingPaymentProcedure(false);
                if (onUpdate) onUpdate(r.booking);
                cxt?.showMessage('S', t('Deleted'));
            },
            // 869d5g0yp: close the modal on error too — otherwise the user
            // is stuck on a dialog with no feedback when the API rejects
            // the cancellation (e.g. 403 on a paymentAction the user is
            // not allowed to perform). Surface the API error message so
            // the user sees why the action failed.
            error: (err: any) => {
                setOpenAskingPaymentProcedure(false);
                cxt?.showMessage('E', getErrorMessage(err));
            },
        });
    };

    /**
     * Download
     */
    const download = (type?: string) => {
        let funcToUse = downloadFile;
        if (type === 'TICKET') {
            funcToUse = openFile as typeof downloadFile;
        }

        funcToUse(
            buildBookingPdfUrl(idBooking, type),
            'application/pdf',
            {} as Record<string, string>,
            'booking.pdf',
        );
    };

    // TODO: mejorar la porquería esta de pedirlo todo en cuanto hay un cambio local

    // He booking has already been cancelled server-side
    function lastOccupantDeleted() {
        if (onUpdate) onUpdate();
        if (onClose) onClose();
    }

    // -----| Render |-----
    return (
        <>
            <SttFullDialog
                open={Boolean(booking && booking.id)}
                onClose={onClose}
                title={t('Booking')}
                bottomButtons={[
                    {
                        caption: t('Booking'),
                        onClick: () => {
                            navigate(getPath('booked', { id: booking?.id }));
                        },
                    },
                    {
                        show: !!(
                            booking &&
                            Object.prototype.hasOwnProperty.call(
                                booking,
                                'slot',
                            )
                        ),
                        caption: t('GoToBooking'),
                        onClick: () => {
                            navigate(
                                getPath('bookingSchedule', {
                                    id: booking?.slot?.schedule.id,
                                }),
                            );
                        },
                    },
                    {
                        show: cutBooking !== null && canModifyBooking,
                        caption: t('Cut'),
                        onClick: () =>
                            booking && cutBooking && cutBooking(booking),
                    },
                    {
                        show: canCancelBooking,
                        type: 'delete',
                        caption: t('CancelBooking'),
                        onClick: () => {
                            // Un alquiler mixto (invitado + usuarios reales) no
                            // puede cancelarse completo: la devolución difiere
                            // por ocupante. Se cancela cada entrada por separado.
                            if (isMixedImpersonal) {
                                cxt?.showMessage(
                                    'E',
                                    t('impersonalTicket.mixedCancelBlocked'),
                                );
                                return;
                            }
                            setOpenAskingPaymentProcedure(!onlyBonos);
                            if (onlyBonos) cancelBooking();
                        },
                    },
                    {
                        show: canModifyBooking,
                        type: 'save',
                        onClick: save,
                    },
                ]}
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: () => download(),
                    },
                    {
                        caption: t('Ticket'),
                        onClick: () => download('TICKET'),
                    },
                ]}
            >
                {booking && (
                    <SttTabs
                        tabs={[
                            {
                                caption: t('Detail'),
                                component: (
                                    <BookingDetailDialog
                                        booking={booking}
                                        setBooking={setBooking}
                                        onUpdate={onUpdate}
                                        onLastOccupantDeleted={
                                            lastOccupantDeleted
                                        }
                                    />
                                ),
                            },
                            {
                                caption: `${t('Payments')} & ${t('Bonos')}`,
                                component: (
                                    <BookingDialogPayments
                                        booking={booking}
                                        onUpdate={(updatedBooking) => {
                                            loadBooking();
                                            if (onUpdate) {
                                                onUpdate(updatedBooking);
                                            }
                                        }}
                                    />
                                ),
                            },
                            {
                                caption: t('DeviceAuths'),
                                component: (
                                    <BookingDialogDeviceAuths
                                        booking={booking}
                                    />
                                ),
                            },
                        ]}
                    />
                )}
            </SttFullDialog>
            {/* <SttDialog
				title={t('Booking')}
				maxWidth="md"
				open={Boolean(booking && booking.id)}
				onClose={onClose}
				content={
					<>
						{booking &&
						<SttTabs
							tabs={[
								{
									caption: t('Detail'),
									component:
										<BookingDetailDialog
											booking={booking}
											setBooking={setBooking}
											onUpdate={onUpdate}
										/>
								},
								{
									caption: `${t('Payments')} & ${t('Bonos')}`,
									component:
										<BookingDialogPayments
											booking={booking}
											onUpdate={onUpdate}
										/>
								},
								{
									caption: t('Occupants'),
									component:
										<BookingDialogOccupants
											booking={booking}
											onUpdate={onUpdate}
											onLastOccupantDeleted={lastOccupantDeleted}
										/>
								},
								{
									caption: t('DeviceAuths'),
									component:
										<BookingDialogDeviceAuths
											booking={booking}
										/>
								},
							]}
						/>}
					</>
				}
				buttons={[
					{
						caption: t('Booking'),
						onClick: () => navigate( getPath('booked', { id: booking.id }) )
					},
					booking && booking.slot && {
						caption: t('GoToBooking'),
						onClick: () => navigate( getPath('bookingSchedule', { id: booking.slot.schedule.id }) )
					},
					{
						show: cutBooking != null,
						caption: t('Cut'),
						onClick: () => cutBooking(booking)
					},
					{
						type: 'delete',
						caption: t('CancelBooking'),
						onClick: () => {setOpenAskingPaymentProcedure(!onlyBonos); if (onlyBonos) cancelBooking();}
					},
					{
						type: 'save',
						onClick: save
					}
				]}
				menu={[
					{
						caption: t('Download') + ' PDF',
							onClick: () => download(),
					},
					{
						caption: t('Ticket'),
						onClick: () => download('TICKET'),
					}
				]}
			/> */}

            {/* --- Modal asking what to do with paymtns --- */}
            <HandlePaymentsModal
                warningText={t('sc.bookings.delete.howToHandlePayments')}
                open={openAskingPaymentProcedure}
                onClose={() => setOpenAskingPaymentProcedure(false)}
                options={buildCancelBookingPaymentOptions(
                    canDeleteBookingPayments(cxt),
                    t,
                    isImpersonal,
                    canRefundDataphone,
                )}
                onAccept={(option: any) => {
                    cancelBooking(paymentActionForOption(option));
                }}
            />
        </>
    );
}
